import React from "react";
import {
  FaCode,
  FaServer,
  FaDatabase,
  FaCloud,
  FaMobile,
  FaLock
} from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHero from "../components/page/PageHero";
import SectionHeading from "../components/page/SectionHeading";
import heroImage from "../assets/solutions/hero-structure.webp";
import ctaImage from "../assets/solutions/cta-foundation.webp";

const Solutions = () => {
  const { t } = useTranslation();

  // Colors for consistent theming
  const primaryTextColor = "text-silver-light"; // Very light gray/silver
  const secondaryTextColor = "text-silver-medium"; // Medium gray/silver

  const [solutionsRef, solutionsInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // Modern web technology architecture solutions
  const webTechSolutions = [
    {
      id: 'frontend',
      icon: <FaCode className="text-5xl mb-6" />,
      title: t('solutions.webTech.frontend.title', "Modern Frontend"),
      description: t('solutions.webTech.frontend.description', "Responsive interfaces built with React, Vue, or Angular frameworks, optimized for performance and user experience.")
    },
    {
      id: 'backend',
      icon: <FaServer className="text-5xl mb-6" />,
      title: t('solutions.webTech.backend.title', "Robust Backend"),
      description: t('solutions.webTech.backend.description', "Scalable server-side solutions using Node.js, Express, Django, or Laravel, designed for reliability and maintainability.")
    },
    {
      id: 'database',
      icon: <FaDatabase className="text-5xl mb-6" />,
      title: t('solutions.webTech.database.title', "Database Design"),
      description: t('solutions.webTech.database.description', "Structured data storage with SQL or NoSQL databases (MongoDB, PostgreSQL, MySQL) optimized for your specific needs.")
    },
    {
      id: 'cloud',
      icon: <FaCloud className="text-5xl mb-6" />,
      title: t('solutions.webTech.cloud.title', "Cloud Services"),
      description: t('solutions.webTech.cloud.description', "Deployment and scaling with AWS, Google Cloud, or Azure services tailored to your project requirements.")
    },
    {
      id: 'mobile',
      icon: <FaMobile className="text-5xl mb-6" />,
      title: t('solutions.webTech.mobile.title', "Mobile Integration"),
      description: t('solutions.webTech.mobile.description', "Cross-platform solutions with React Native or responsive web design for seamless mobile experiences.")
    },
    {
      id: 'security',
      icon: <FaLock className="text-5xl mb-6" />,
      title: t('solutions.webTech.security.title', "Security & Performance"),
      description: t('solutions.webTech.security.description', "Built-in security practices and performance optimization to protect data and provide fast user experiences.")
    }
  ];

  return (
    <div className="bg-black font-sans">
      <SEO
        title="Web Development Solutions | Custom Websites, Web Apps & Secure Architecture"
        description="Web development solutions for businesses that need custom websites, web applications, and secure scalable architecture. Designed for performance, conversion, and long-term growth."
        keywords="web development solutions, custom website development, web application development, website developer for business, custom software development, secure web architecture"
        url="/solutions"
      />
      {/* The CTA label is deliberately NOT "Get Started". PageHero's own notes
          record the NN/g finding: generic labels get clicked but mislead people
          about where they land, so clicks rise while qualified leads fall.
          "Book a free 30-minute consultation" says exactly what happens next. */}
      <PageHero
        eyebrow={t('solutions.hero.eyebrow', 'Solutions')}
        title={t('solutions.hero.title', 'Tailored Solutions')}
        subtitle={t('solutions.hero.subtitle', 'We craft innovative digital platforms and provide expert guidance to empower your business, streamline operations, and drive growth in a competitive landscape.')}
        ctaLabel={t('solutions.hero.cta', 'Book a free 30-minute consultation')}
        ctaTo="/contact"
        secondaryLabel={t('solutions.hero.secondary', 'See the stack')}
        secondaryHref="#stack"
        proofLine={t('solutions.hero.proof', 'Rated 5.0 on Google · Free consultation · Fixed price agreed before work starts')}
        backgroundImage={heroImage}
      />

      {/* Web Technology Architecture Section */}
      <section
        id="stack"
        ref={solutionsRef}
        className="scroll-mt-24 py-20 bg-black"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div
              className={`mb-16 transition-all duration-1000 transform ${solutionsInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
            >
              <SectionHeading
                eyebrow={t('solutions.webTech.eyebrow', 'How it is built')}
                title={t('solutions.webTech.title', 'Modern Web Technology Architecture')}
                lead={t('solutions.webTech.subtitle', 'We build your digital solutions using cutting-edge technologies that ensure scalability, performance, and maintainability.')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {webTechSolutions.map((solution, index) => (
                <div
                  key={solution.id}
                  className={`transition-all duration-1000 transform ${solutionsInView
                    ? "translate-y-0 opacity-100"
                    : "translate-y-10 opacity-0"
                    }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="h-full rounded-lg border border-gray-800 bg-off-black p-8 transition-colors duration-300 hover:border-moss/50">
                    <div className="text-moss-light">
                      {solution.icon}
                    </div>
                    <h3 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>
                      {solution.title}
                    </h3>
                    <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                      {solution.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h3 className="text-[11px] uppercase tracking-[0.16em] text-silver-dark mb-2">
              Related pages
            </h3>
            <p className="text-xs md:text-sm text-silver-medium leading-relaxed">
              <Link to="/services" className="text-silver-dark hover:text-silver-medium transition-colors">
                Services
              </Link>
              {" · "}
              <Link to="/custom-development" className="text-silver-dark hover:text-silver-medium transition-colors">
                Custom development
              </Link>
              {" · "}
              <Link to="/business-websites" className="text-silver-dark hover:text-silver-medium transition-colors">
                Business websites
              </Link>
              {" · "}
              <Link to="/contact" className="text-silver-dark hover:text-silver-medium transition-colors">
                Contact
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden">
        <img
          src={ctaImage}
          alt=""
          aria-hidden="true"
          className="h-[26rem] w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4 text-center">
            <div className="mx-auto max-w-3xl">
              <h2 className={`mb-4 text-3xl md:text-4xl font-serif font-medium ${primaryTextColor}`}>
                {t('solutions.cta.title', 'Ready to Build Your Solution?')}
              </h2>
              <p className={`${secondaryTextColor} mb-8`}>
                {t('solutions.cta.text', "Let's discuss how our technology stack can meet your specific business goals and requirements.")}
              </p>
              <Link
                to="/contact"
                className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
              >
                {t('solutions.cta.button', 'Book a free 30-minute consultation')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;